import { useState, useEffect, useCallback } from "react";
import Select, {
  MultiValue,
  StylesConfig,
  components,
  MenuListProps,
} from "react-select";
import { PolicyAxiosInstance } from "../../Network/PolicyAxiosInstance";
import { Option } from "../../interface/Option";

interface PlanPolicySelectorProps {
  onChange: (selected: Option[]) => void;
  clear?: boolean;
  defaultValue?: Option[];
  value?: Option[];
}

const PlanPolicySelector = ({
  onChange,
  clear,
  defaultValue,
  value,
}: PlanPolicySelectorProps) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>(
    value || defaultValue || []
  );
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");

  const loadOptions = useCallback(async () => {
    setLoading(true);

    try {
      const response = await PolicyAxiosInstance.get("searchpolicy", {
        params: {
          name: inputValue,
        },
      });

      console.log("API Response:", response.data.result);

      const data = response.data?.result || [];

      if (!data.length) {
        console.warn("No policy received from API");
      }

      const formattedOptions: Option[] = data.map((item: any) => {
        const policyId =
          item.policy_id ??
          item.Policy_id ??
          item.id ??
          "";

        const policyName =
          item.policy_name ??
          item.Policy_name ??
          item.name ??
          "";

        return {
          value: String(policyId),
          label: policyName
            ? `${policyId} - ${policyName}`
            : String(policyId),
        };
      });

      console.log("Formatted Options:", formattedOptions);
      setOptions(formattedOptions);
    } catch (error) {
      console.error("Error loading policies:", error);
    } finally {
      setLoading(false);
    }
  }, [inputValue]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      loadOptions();
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [inputValue, loadOptions]);

  const handleChange = (selected: MultiValue<Option>) => {
    const selectedValues = selected as Option[];
    setSelectedOptions(selectedValues);

    if (typeof onChange === "function") {
      onChange(selectedValues);
    } else {
      console.error("onChange is not a function");
    }
  };

  useEffect(() => {
    if (clear) {
      setSelectedOptions([]);
    }
  }, [clear]);

  useEffect(() => {
    if (value) {
      setSelectedOptions(value);
    }
  }, [value]);

  useEffect(() => {
    if (!value && defaultValue) {
      setSelectedOptions(defaultValue);
    }
  }, [defaultValue, value]);

  const MenuList = (props: MenuListProps<Option, true>) => (
    <components.MenuList {...props}>
      <div
        onWheelCapture={(e) => e.stopPropagation()}
        onTouchMoveCapture={(e) => e.stopPropagation()}
      >
        {props.children}
      </div>
    </components.MenuList>
  );

  const customStyles: StylesConfig<Option, true> = {
    control: (styles) => ({
      ...styles,
      minHeight: "40px",
      fontSize: "14px",
      borderRadius: "8px",
      cursor: "pointer",
    }),

    valueContainer: (styles) => ({
      ...styles,
      fontSize: "14px",
    }),

    input: (styles) => ({
      ...styles,
      fontSize: "14px",
    }),

    multiValue: (styles) => ({
      ...styles,
      backgroundColor: "#007BFF",
      borderRadius: "4px",
      padding: "2px 4px",
    }),

    multiValueLabel: (styles) => ({
      ...styles,
      color: "#FFFFFF",
      fontSize: "12px",
      padding: "0 4px",
    }),

    multiValueRemove: (styles) => ({
      ...styles,
      color: "#FFFFFF",
      padding: "2px",
      cursor: "pointer",
      ":hover": {
        backgroundColor: "#0056b3",
        color: "#FFFFFF",
      },
    }),

    option: (styles, { isSelected, isFocused }) => ({
      ...styles,
      backgroundColor: isSelected
        ? "#007BFF"
        : isFocused
        ? "#E6F0FA"
        : "#FFFFFF",
      color: isSelected ? "#FFFFFF" : "#000000",
      fontSize: "14px",
      padding: "8px 10px",
      cursor: "pointer",
      ":hover": {
        backgroundColor: "#0056b3",
        color: "#FFFFFF",
      },
    }),

    placeholder: (styles) => ({
      ...styles,
      fontSize: "14px",
      color: "#6B7280",
    }),

    menu: (styles) => ({
      ...styles,
      zIndex: 999999,
      pointerEvents: "auto",
    }),

    menuPortal: (styles) => ({
      ...styles,
      zIndex: 999999,
      pointerEvents: "auto",
    }),

    menuList: (styles) => ({
      ...styles,
      maxHeight: "220px",
      overflowY: "auto",
      pointerEvents: "auto",
    }),
  };

  return (
    <Select
      isMulti
      value={selectedOptions}
      onChange={handleChange}
      options={options}
      isLoading={loading}
      onInputChange={(newValue) => {
        setInputValue(newValue);
        return newValue;
      }}
      placeholder="Select Policy..."
      styles={customStyles}
      components={{ MenuList }}
      menuPortalTarget={
        typeof document !== "undefined" ? document.body : undefined
      }
      menuPosition="fixed"
      menuPlacement="bottom"
      menuShouldScrollIntoView={false}
      closeMenuOnSelect={false}
      blurInputOnSelect={false}
      captureMenuScroll={false}
      classNamePrefix="plan-policy-select"
    />
  );
};

export default PlanPolicySelector;